import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { forwardRef } from 'react';
import type { StatusProps, StatusType, StatusSize, StatusVariant } from './Status.types';
import type { Theme } from '../../theme';

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const getTypeColors = (type: StatusType, theme: Theme) => {
  switch (type) {
    case 'success':
      return {
        bg: theme.colors.semantic.green.base,
        bgLight: theme.colors.semantic.green.lighter,
        text: theme.colors.semantic.green.dark,
        border: theme.colors.semantic.green.base,
      };
    case 'error':
      return {
        bg: theme.colors.semantic.red.base,
        bgLight: theme.colors.semantic.red.lighter,
        text: theme.colors.semantic.red.dark,
        border: theme.colors.semantic.red.base,
      };
    case 'warning':
      return {
        bg: theme.colors.semantic.orange.base,
        bgLight: theme.colors.semantic.orange.lighter,
        text: theme.colors.semantic.orange.dark,
        border: theme.colors.semantic.orange.base,
      };
    case 'pending':
      return {
        bg: theme.colors.semantic.orange.light,
        bgLight: theme.colors.semantic.orange.lighter2,
        text: theme.colors.semantic.orange.dark,
        border: theme.colors.semantic.orange.light,
      };
    case 'info':
      return {
        bg: theme.colors.semantic.blue.base,
        bgLight: theme.colors.semantic.blue.lighter,
        text: theme.colors.semantic.blue.dark,
        border: theme.colors.semantic.blue.base,
      };
    case 'neutral':
    default:
      return {
        bg: theme.colors.grey[500],
        bgLight: theme.colors.grey[100],
        text: theme.colors.grey[700],
        border: theme.colors.grey[500],
      };
  }
};

const getSizeStyles = (size: StatusSize, variant: StatusVariant) => {
  if (variant === 'dot') {
    switch (size) {
      case 'small':
        return css`
          .status-dot {
            width: 6px;
            height: 6px;
          }
          font-size: 12px;
          gap: 6px;
        `;
      case 'large':
        return css`
          .status-dot {
            width: 10px;
            height: 10px;
          }
          font-size: 14px;
          gap: 10px;
        `;
      case 'medium':
      default:
        return css`
          .status-dot {
            width: 8px;
            height: 8px;
          }
          font-size: 13px;
          gap: 8px;
        `;
    }
  }

  switch (size) {
    case 'small':
      return css`
        height: 20px;
        font-size: 11px;
        padding: 0 6px;
        gap: 4px;
        
        .status-icon {
          width: 12px;
          height: 12px;
        }
      `;
    case 'large':
      return css`
        height: 28px;
        font-size: 14px;
        padding: 0 10px;
        gap: 6px;
        
        .status-icon {
          width: 18px;
          height: 18px;
        }
      `;
    case 'medium':
    default:
      return css`
        height: 24px;
        font-size: 12px;
        padding: 0 8px;
        gap: 5px;
        
        .status-icon {
          width: 14px;
          height: 14px;
        }
      `;
  }
};

const getVariantStyles = (type: StatusType, variant: StatusVariant, theme: Theme) => {
  const colors = getTypeColors(type, theme);

  switch (variant) {
    case 'outlined':
      return css`
        background-color: transparent;
        border: 1px solid ${colors.border};
        color: ${colors.text};
        
        .status-icon {
          color: ${colors.bg};
        }
      `;
    case 'dot':
      return css`
        background-color: transparent;
        border: none;
        color: ${colors.text};
        padding: 0;
        height: auto;
        
        .status-dot {
          background-color: ${colors.bg};
        }
      `;
    case 'filled':
    default:
      return css`
        background-color: ${colors.bgLight};
        border: none;
        color: ${colors.text};
        
        .status-icon {
          color: ${colors.bg};
        }
      `;
  }
};

interface StatusContainerProps {
  theme?: Theme;
  type: StatusType;
  size: StatusSize;
  variant: StatusVariant;
  isPulsing: boolean;
}

const StatusContainer = styled.span<StatusContainerProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-family: inherit;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  box-sizing: border-box;
  
  ${props => getSizeStyles(props.size, props.variant)}
  ${props => getVariantStyles(props.type, props.variant, props.theme as Theme)}
  
  ${props => props.isPulsing && css`
    animation: ${pulse} 2s ease-in-out infinite;
  `}
`;

const Dot = styled.span`
  border-radius: 50%;
  flex-shrink: 0;
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SuccessIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.5 4.5L6.5 11.5L3 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 4L4 12M4 4L12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 5.5V8.5M8 11H8.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.86 2.57L1.21 12.29C1.07 12.54 1 12.82 1 13.12C1 13.41 1.07 13.69 1.21 13.94C1.35 14.19 1.55 14.41 1.79 14.56C2.03 14.72 2.31 14.8 2.6 14.8H13.4C13.69 14.8 13.97 14.72 14.21 14.56C14.45 14.41 14.65 14.19 14.79 13.94C14.93 13.69 15 13.41 15 13.12C15 12.82 14.93 12.54 14.79 12.29L9.14 2.57C9 2.32 8.8 2.12 8.56 1.98C8.32 1.84 8.05 1.77 7.77 1.77C7.49 1.77 7.22 1.84 6.98 1.98C6.74 2.12 6.54 2.32 6.4 2.57H6.86Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PendingIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" />
    <path
      d="M8 4V8L10.5 10.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" />
    <path
      d="M8 7V11M8 5H8.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NeutralIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" />
    <path
      d="M5 8H11"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const getDefaultIcon = (type: StatusType) => {
  switch (type) {
    case 'success':
      return <SuccessIcon />;
    case 'error':
      return <ErrorIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'pending':
      return <PendingIcon />;
    case 'info':
      return <InfoIcon />;
    case 'neutral':
    default:
      return <NeutralIcon />;
  }
};

export const Status = forwardRef<HTMLSpanElement, StatusProps>(
  (
    {
      type,
      label,
      size = 'medium',
      variant = 'filled',
      icon,
      showIcon = true,
      pulse = false,
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    const displayIcon = icon !== undefined ? icon : getDefaultIcon(type);
    const shouldPulse = pulse || type === 'pending';

    return (
      <StatusContainer
        ref={ref}
        type={type}
        size={size}
        variant={variant}
        isPulsing={shouldPulse && variant !== 'dot'}
        className={className}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        {variant === 'dot' ? (
          <Dot className="status-dot" />
        ) : (
          showIcon && displayIcon && (
            <IconWrapper className="status-icon" aria-hidden="true">
              {displayIcon}
            </IconWrapper>
          )
        )}
        {label && <Label>{label}</Label>}
      </StatusContainer>
    );
  }
);

Status.displayName = 'Status';
